import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, Params, Router, RouterStateSnapshot } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Observable, Subscriber } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { ConfigService } from '../services/config.service';
import { LoaderService } from '../services/loader.service';
import { NotifyOrConfirmService } from '../services/notify-or-confirm.service';
import { SessionService } from '../services/session.service';

@Injectable()
export class SessionLoginGuard implements CanActivate {
	constructor(
		private authService: AuthService,
		private sessionService: SessionService,
		private router: Router,
		private notifyOrConfirmService: NotifyOrConfirmService,
		private translate: TranslateService,
		private configService: ConfigService,
		protected loaderService: LoaderService
	) {}

	/**
	 * Can current user activates the page
	 *
	 * @param route - Active rout
	 * @param state - Router state
	 * @return
	 */
	canActivate(route: ActivatedRouteSnapshot, state: RouterStateSnapshot): Observable<boolean> {
		const redirectUrl = route.data['guardRedirectUrl'];
		return this.guardHandling(state, redirectUrl);
	}

	/**
	 * Guard handling
	 *
	 * @param state Current router state
	 * @param redirectUrl
	 * @returns
	 */
	private guardHandling(state: RouterStateSnapshot, redirectUrl?: string): Observable<boolean> {
		// Return Observable with can activated flag
		return new Observable<boolean>((subscriber) => {
			// Process external session id
			const externalSessionId = state.root.queryParams[this.configService.config.externalSessionIdParameterKey];

			// Get params
			const queryParams = JSON.parse(JSON.stringify(state.root.queryParams));

			// Process session login
			if (externalSessionId) {
				// Remove external session id param
				delete queryParams[this.configService.config.externalSessionIdParameterKey];

				// Check if user is logged in
				this.authService.loggedIn(true, false).subscribe((loggedIn: boolean) => {
					// A user is logged in
					if (loggedIn) {
						// Check whether the current user is the session user
						this.authService.isUsersSession(externalSessionId).subscribe((isUsersSession) => {
							// Session id doesn't belong to current user
							if (!isUsersSession) {
								try {
									// End session of current user
									this.authService.endSession().subscribe();

									// Login via external session ID
									this.loginViaSession(externalSessionId, state, queryParams, subscriber);
								} catch (e) {
									this.authService.logout().subscribe();
								}

								// Session id belongs to current user
							} else {
								// Navigate to same page without external session parameter
								this.router.navigate([state.url.split('?')[0]], { queryParams });
							}
						});

						// No user is logged in
					} else {
						// Login via session
						this.loginViaSession(externalSessionId, state, queryParams, subscriber);
					}
				});

				// No external session id
			} else {
				subscriber.next(true);
				subscriber.complete();
			}
		});
	}

	/**
	 * Login via session
	 */
	private loginViaSession(externalSessionId: string, state: RouterStateSnapshot, queryParams: Params, subscriber: Subscriber<boolean>) {
		this.loaderService.externalLoader.loadUntilComplete(this.authService.loginViaSessionId(externalSessionId, true)).subscribe(
			() => {
				// Navigate to same page without external session parameter
				this.router.navigate([state.url.split('?')[0]], { queryParams });
				subscriber.next(true);
				subscriber.complete();
			},
			(err) => {
				subscriber.error(err);
			}
		);
	}
}
